import * as React from "react";
import { useAuth } from "@bi-studio/shared/hooks";
import {
  Button,
  Modal,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
  Group,
  LoadingOverlay,
  Box,
  ThemeIcon,
  Badge,
  Tree,
  useTree,
  useMantineColorScheme,
  type TreeNodeData
} from "@mantine/core";
import { FolderIcon } from "@bi-studio/shared/components";
import type { FolderTreeNode } from "@bi-studio/shared/lib";
import { getFolders } from "@bi-studio/shared/apis";
import type { SelectedItem } from "../FolderActionToolbar";

interface MoveItemsModalProps {
  selectedItems: SelectedItem[];
  currentFolderId: string | null; // null for root folder
  onMove: (targetFolderId: string | null) => Promise<void>;
  onClose: () => void;
}

export function MoveItemsModal({
  selectedItems,
  currentFolderId,
  onMove,
  onClose,
}: MoveItemsModalProps) {
  const { token } = useAuth();
  const { colorScheme } = useMantineColorScheme();
  const [folders, setFolders] = React.useState<FolderTreeNode[]>([]);
  const [selectedFolderId, setSelectedFolderId] = React.useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const tree = useTree();

  React.useEffect(() => {
    if (!token) return;

    const loadFolders = async () => {
      try {
        const data = await getFolders(token);
        setFolders(data);
      } catch (err) {
        console.error("Failed to load folders:", err);
      }
    };

    loadFolders();
  }, [token]);

  const handleMove = async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      setError(null);
      await onMove(selectedFolderId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to move items");
    } finally {
      setIsLoading(false);
    }
  };

  // Get all descendant folder IDs for a set of parent IDs
  const getDescendantIdsForFolders = React.useCallback(
    (folderNodes: FolderTreeNode[], parentIds: Set<string>): Set<string> => {
      const descendants = new Set<string>();
      const findDescendants = (nodes: FolderTreeNode[], isParentBeingMoved: boolean) => {
        for (const node of nodes) {
          const moving = isParentBeingMoved || parentIds.has(node.id);
          if (moving) {
            descendants.add(node.id);
          }
          if (node.children && node.children.length > 0) {
            findDescendants(node.children, moving);
          }
        }
      };
      findDescendants(folderNodes, false);
      return descendants;
    },
    []
  );

  // Get IDs of folders being moved
  const foldersBeingMoved = React.useMemo(() => {
    return new Set(
      selectedItems.filter((item) => item.type === "folder").map((item) => item.id)
    );
  }, [selectedItems]);

  const forbiddenIds = React.useMemo(() => {
    return getDescendantIdsForFolders(folders, foldersBeingMoved);
  }, [folders, foldersBeingMoved, getDescendantIdsForFolders]);

  const transformFolderData = React.useCallback((nodes: FolderTreeNode[]): TreeNodeData[] => {
    return nodes
      .filter(node => !forbiddenIds.has(node.id))
      .map(node => ({
        label: node.name,
        value: node.id,
        children: node.children && node.children.length > 0 ? transformFolderData(node.children) : undefined
      }));
  }, [forbiddenIds]);

  const treeData = React.useMemo(() => transformFolderData(folders), [folders, transformFolderData]);

  const selectedCount = selectedItems.length;
  const itemTypes = selectedItems.reduce(
    (acc, item) => {
      acc[item.type] = (acc[item.type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title="Move Items"
      size="lg"
      centered
      overlayProps={{ backgroundOpacity: 0.1, blur: 2 }}
    >
      <LoadingOverlay visible={isLoading} overlayProps={{ backgroundOpacity: 0.1, blur: 2 }} />
      <Stack>
        <Text size="sm" c={colorScheme === 'dark' ? 'dimmed' : 'gray.7'}>
          Select a destination folder for {selectedCount} item{selectedCount !== 1 ? "s" : ""}
        </Text>

        <Box
          p="xs"
          style={{
            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
            border: '1px solid var(--modal-border)',
            borderRadius: 'var(--mantine-radius-md)',
            backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined
          }}
        >
          <Text size="xs" fw={500} mb={4}>Selected items:</Text>
          <Group gap="xs">
            {Object.entries(itemTypes).map(([type, count]) => (
              <Badge key={type} size="sm" variant="outline" color="gray">
                {String(count)} {type}{count !== 1 ? "s" : ""}
              </Badge>
            ))}
          </Group>
        </Box>

        <ScrollArea.Autosize mah={400} offsetScrollbars>
          <Stack gap={2}>
            <UnstyledButton
              onClick={() => setSelectedFolderId(null)}
              className="w-full flex items-center gap-2 p-2 rounded-md transition"
              style={{
                backgroundColor: selectedFolderId === null
                  ? (colorScheme === 'dark' ? 'var(--mantine-color-gray-8)' : 'var(--mantine-color-brand-filled)')
                  : undefined,
                color: selectedFolderId === null ? 'var(--mantine-color-white)' : undefined,
              }}
            >
              <ThemeIcon size="sm" variant="transparent" color={selectedFolderId === null ? "white" : "gray"}>
                <FolderIcon size={16} />
              </ThemeIcon>
              <Text size="sm" fw={500}>Root Folder</Text>
            </UnstyledButton>

            <Tree
              tree={tree}
              data={treeData}
              renderNode={({ node, elementProps, level }) => {
                const isSelected = selectedFolderId === node.value;
                const isForbidden = node.value === currentFolderId;

                return (
                  <Group
                    gap={8}
                    {...elementProps}
                    onClick={() => {
                      if (!isForbidden) {
                        setSelectedFolderId(node.value);
                      }
                      tree.toggleExpanded(node.value);
                    }}
                    style={{
                      ...elementProps.style,
                      cursor: isForbidden ? 'default' : 'pointer',
                      padding: '4px 8px',
                      paddingLeft: `calc(${level} * 24px + 8px)`,
                      borderRadius: '4px',
                      backgroundColor: isSelected
                        ? (colorScheme === 'dark' ? 'var(--mantine-color-gray-8)' : 'var(--mantine-color-brand-filled)')
                        : undefined,
                      color: isSelected ? 'var(--mantine-color-white)' : (isForbidden ? 'var(--mantine-color-dimmed)' : undefined),
                      opacity: isForbidden ? 0.6 : 1,
                    }}
                  >
                    <ThemeIcon
                      variant="subtle"
                      color={isSelected ? 'white' : 'gray'}
                      size="sm"
                    >
                      <FolderIcon size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={isSelected ? 500 : 400}>
                      {node.label}
                    </Text>
                  </Group>
                );
              }}
            />
          </Stack>
        </ScrollArea.Autosize>

        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}

        <Group justify="flex-end" pt="md">
          <Button
            variant={colorScheme === 'dark' ? 'light' : 'subtle'}
            onClick={onClose}
            disabled={isLoading}
            color="gray"
          >
            Cancel
          </Button>
          <Button
            onClick={handleMove}
            disabled={isLoading}
            loading={isLoading}
            variant="filled"
            color={colorScheme === 'dark' ? 'gray.8' : 'brand'}
          >
            Move
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
